import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { PhoneType } from "@/Common/Models/Contacts/Enums/PhoneType";
import { PhoneNumber } from "@/Common/Models/Contacts/PhoneNumber";
import { Patient } from "@/Common/Models/Patient/Patient";
import { ObstetricalFileControlRenderer } from "@/Patient/Components/MedicalNote/ObstetricalFile/ControlRenderer/ObstetricalFileControlRenderer";
import { QuebecObstetricalFileFieldLimits } from "@/Patient/Components/MedicalNote/ObstetricalFile/Form/Quebec/QuebecObstetricalFileFieldLimits";
import { Encounter } from "@/Patient/Models/MedicalSummary/Encounter";

interface IQuebecObstetricalFilePageHeaderProps {
    summary?: string;
    patient: Patient;
    encounter: Encounter;

    isExtended: boolean;
    readonly?: boolean;

    onHeaderToggle: () => void;
    onSummaryChange: (summary: string) => void;
    onSummaryBlur: () => void;
}

class QuebecObstetricalFilePageHeader extends BaseReactComponent<IQuebecObstetricalFilePageHeaderProps> {
    private componentId: string = "obstetrical-file-header";

    public render(): JSX.Element {
        if (!this.props)
            return null;

        const linkLabel = this.props.isExtended ? "ViewLessLabel" : "ViewMoreLabel";

        return (
            <div id={this.componentId}>
                <div className="patient-clinic-header">
                    <h4 className="inline-block">{cacheStore.GetString("PatientAndClinicInformationLabel")}</h4>
                    <a className="marginleft10" onClick={() => this.props.onHeaderToggle?.()}>
                        {cacheStore.GetString(linkLabel)}
                    </a>
                </div>
                {this.renderMainSection()}
            </div>
        );
    }

    private renderMainSection(): JSX.Element {
        if (!this.props.isExtended)
            return (
                <div className="row" />
            );

        return (
            <div className="row border border-top">
                <div className="col-xs-6 border border-right">
                    {this.renderPatientInfo()}
                </div>
                <div className="col-xs-6">
                    {this.renderClinicInfo()}
                </div>
            </div>
        );
    }

    private renderPatientInfo(): JSX.Element {
        if (!this.props.patient)
            return null;

        let patient = new Patient(this.props.patient);

        return (
            <div className="section section-patient">
                <div className="row">
                    {this.renderInfo("col-md-4 padding-none", "FamilyNameAtBirthLabel", patient.IndividualResponse?.LastName, QuebecObstetricalFileFieldLimits.Page1.PatientLastNameAtBirth.MaxChars)}
                    {this.renderInfo("col-md-8", "GivenNamesLabel", patient.IndividualResponse?.FirstName, QuebecObstetricalFileFieldLimits.Page1.PatientFirstName.MaxChars)}
                </div>

                <div className="row">
                    {this.renderInfo("col-md-4 padding-none", "DateofBirthLabel", patient.GetFormattedDateOfBirth())}
                    {this.renderInfo("col-md-4", "AgeLabel", patient.getAgeFromEncounterDate(this.props.encounter?.ConsultationDate)?.toString(), 3)}
                    {this.renderInfo("col-md-4", "TelephoneNoLabel", new PhoneNumber(patient.Phones?.firstPreferredOrDefault(x => x.IsPreferred)).format("({1}) {2} {3}"))}
                </div>

                <div className="row">
                    {this.renderInfo("col-md-12 padding-none", "HealthInsuranceLabel", patient.GetFormattedNAM(), QuebecObstetricalFileFieldLimits.Page1.PatientHealthInsuranceNumber.MaxChars)}
                </div>

                <div className="row">
                    {this.renderInfo("col-md-12 padding-none", "AddressLabel", patient.getFullFormattedAddress(), QuebecObstetricalFileFieldLimits.Page1.PatientFullFormattedAddress.MaxChars)}
                </div>
            </div>
        );
    }

    private renderInfo(className: string, label: string, info: string, textLength: number = null): JSX.Element {
        const infoText = textLength ? limitTextLength(info, textLength) : info;

        return (
            <div className={className}>
                <label className="field-label">{cacheStore.GetString(label)}</label>
                <span className="field-text">{infoText}</span>
            </div>
        );
    }

    private renderClinicInfo(): JSX.Element {
        if (!this.props.encounter)
            return null;

        const site = cacheStore.GetSiteById(this.props.encounter.SiteId);
        if (!site)
            return null;

        return (
            <div className="section section-clinic">
                <div className="row">
                    <h3>{site.Model?.ClinicalName}</h3>
                </div>
                <div className="row">
                    <p>{site.getFormattedAddress()}</p>
                </div>
                <div className="row">
                    <div className="phone"><strong>{cacheStore.GetString("PhoneLabel")}</strong>{site.getFormattedPhoneByType(PhoneType.Phone)}</div>
                    <div className="phone"><strong>{cacheStore.GetString("FaxLabel")}</strong>{site.getFormattedPhoneByType(PhoneType.Fax)}</div>
                </div>
                <div className="row">
                    {this.renderSummary()}
                </div>
            </div>
        );
    }

    private renderSummary(): JSX.Element {
        const controlRenderer = new ObstetricalFileControlRenderer({
            readonly: this.props.readonly,
            onBlur: () => this.props.onSummaryBlur?.()
        });
        return (
            controlRenderer.renderTextarea({
                value: this.props.summary,
                onChange: value => this.props.onSummaryChange?.(value),
                id: `${this.componentId}_${sessionStore.newId()}`,
                maxLength: 138,
                maxRows: 5
            })
        );
    }
}

export { QuebecObstetricalFilePageHeader };
